import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, AlertCircle } from 'lucide-react';
import LandingNavbar from '../components/LandingNavbar';
import { useAuth } from '../context/AuthContext';

/**
 * /force-change-password
 *
 * Shown to users who authenticated via a welcome magic link (or regular
 * login if we ever redirect here directly). Calls /auth/set-initial-password
 * which doesn't require the current password — acceptable because the
 * caller already proved identity via the valid session token.
 */
export default function ForceChangePassword() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!newPassword || !confirmNewPassword) {
      setError('يرجى تعبئة جميع الحقول');
      return;
    }
    if (newPassword.length < 8) {
      setError('كلمة المرور يجب أن تكون 8 أحرف على الأقل');
      return;
    }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      setError('يجب أن تحتوي على حرف كبير وصغير ورقم');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError('كلمتا المرور غير متطابقتين');
      return;
    }

    setLoading(true);
    try {
      const axios = (await import('axios')).default;
      const apiBase = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
      const token = localStorage.getItem('token');
      await axios.post(
        `${apiBase}/auth/set-initial-password`,
        { newPassword },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Patch the cached user so ProtectedRoute doesn't bounce us around.
      try {
        const cached = JSON.parse(localStorage.getItem('user') || '{}');
        cached.must_change_password = false;
        localStorage.setItem('user', JSON.stringify(cached));
      } catch { /* ignore */ }

      navigate('/', { replace: true });
      // Hard reload so AuthContext + permissions rehydrate cleanly.
      window.location.reload();
    } catch (err) {
      setError(err?.response?.data?.message || 'تعذّر تغيير كلمة المرور');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LandingNavbar />
      <div
        className="min-h-screen flex items-center justify-center px-6 pt-[120px] pb-16 relative"
        dir="rtl"
        style={{ backgroundColor: '#FDFCF9' }}
      >
        <div
          className="absolute inset-0 opacity-[0.4] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #0F6E56 0.8px, transparent 0)',
            backgroundSize: '28px 28px',
            maskImage: 'radial-gradient(ellipse at center, rgba(0,0,0,0.25), transparent 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center, rgba(0,0,0,0.25), transparent 70%)',
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 w-full max-w-[440px] bg-white rounded-3xl border border-[#E8E5DD] shadow-[0_10px_40px_-10px_rgba(15,110,86,0.12)] p-8 md:p-10"
        >
          <div className="flex justify-center mb-5">
            <img src="/listech-logo.svg" alt="LIS Techs" className="h-9 w-auto" />
          </div>

          <div className="text-center mb-6">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
              style={{ backgroundColor: '#FEF3C7' }}
            >
              <Lock className="w-6 h-6" style={{ color: '#B45309' }} />
            </div>
            <h2
              className="text-[1.4rem] font-bold mb-1"
              style={{ color: '#1A1A1A', fontFamily: "'Readex Pro', sans-serif" }}
            >
              عيّن كلمة المرور الجديدة
            </h2>
            <p className="text-sm leading-[1.7]" style={{ color: '#8A8A8A' }}>
              {user?.name ? `مرحباً ${user.name}، ` : ''}
              لحمايتك — اختر كلمة مرور شخصية قوية قبل الدخول للنظام.
            </p>
          </div>

          {error && (
            <div
              className="mb-5 p-3.5 rounded-xl flex items-center gap-3 text-sm border"
              style={{ backgroundColor: '#FEF2F2', borderColor: '#FECACA', color: '#B91C1C' }}
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#444' }}>
                كلمة المرور الجديدة
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full h-12 px-4 pr-11 rounded-xl text-sm border outline-none transition-all duration-200"
                  style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E5E5', color: '#1A1A1A' }}
                  onFocus={(e) => { e.target.style.borderColor = '#0F6E56'; e.target.style.boxShadow = '0 0 0 3px rgba(15,110,86,0.08)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#E5E5E5'; e.target.style.boxShadow = 'none'; }}
                  placeholder="8 أحرف على الأقل"
                  dir="ltr"
                  autoFocus
                />
                <Lock className="absolute right-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px]" style={{ color: '#BFBFBF' }} />
              </div>
              <p className="text-[11.5px] mt-1.5" style={{ color: '#8A8A8A' }}>
                حرف كبير + حرف صغير + رقم
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#444' }}>
                تأكيد كلمة المرور
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="w-full h-12 px-4 pr-11 rounded-xl text-sm border outline-none transition-all duration-200"
                  style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E5E5', color: '#1A1A1A' }}
                  onFocus={(e) => { e.target.style.borderColor = '#0F6E56'; e.target.style.boxShadow = '0 0 0 3px rgba(15,110,86,0.08)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#E5E5E5'; e.target.style.boxShadow = 'none'; }}
                  placeholder="••••••••"
                  dir="ltr"
                />
                <Lock className="absolute right-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px]" style={{ color: '#BFBFBF' }} />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl text-sm font-bold text-white transition-colors"
              style={{
                background: 'linear-gradient(135deg, #0F6E56 0%, #1D9E75 100%)',
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? 'جارٍ الحفظ...' : 'حفظ كلمة المرور والدخول'}
            </button>
          </form>

          <button
            type="button"
            onClick={async () => { await logout(); navigate('/staff-login', { replace: true }); }}
            className="w-full mt-4 text-sm text-center"
            style={{ color: '#8A8A8A' }}
          >
            تسجيل الخروج
          </button>
        </motion.div>
      </div>
    </>
  );
}
